/* Adorable Bliss - "Love notes from the Bliss Family"
   (sections/ab-love-notes.liquid), 25 Oct 2026.
   Arrows, product filter, progress line, reveal on scroll and the popup.
   No cart, form or checkout code: the popup's button is a plain link to the
   product page. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (root) => {
    if (!root || root.dataset.abReady) return;
    root.dataset.abReady = '1';
    const track = root.querySelector('[data-track]');
    const cards = [...root.querySelectorAll('[data-card]')];
    const thumb = root.querySelector('[data-progress]');
    const arrows = [...root.querySelectorAll('.ab-love__nav .ab-love__arrow')];
    const chips = [...root.querySelectorAll('.ab-love__chip')];
    const dialog = root.querySelector('dialog');
    if (!track || !cards.length) return;
    const visible = () => cards.filter((c) => !c.hidden);

    /* Progress line + arrow state */
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const ratio = max > 1 ? track.clientWidth / track.scrollWidth : 1;
      const pos = max > 1 ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 0;
      if (thumb) {
        thumb.style.width = `${ratio * 100}%`;
        thumb.style.transform = `translateX(${(pos * (1 - ratio) * 100) / ratio}%)`;
      }
      if (arrows.length === 2) {
        arrows[0].disabled = track.scrollLeft <= 2;
        arrows[1].disabled = track.scrollLeft >= max - 2;
      }
      root.classList.toggle('is-static', max <= 1);
    };
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();

    const step = () => {
      const c = visible()[0];
      return c ? c.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : 300;
    };
    let holdUntil = 0;
    const hold = () => { holdUntil = Date.now() + 6000; };
    arrows.forEach((b) => b.addEventListener('click', () => {
      hold();
      track.scrollBy({ left: +b.dataset.dir * step(), behavior: reduceMotion ? 'auto' : 'smooth' });
    }));

    /* Auto-rotate: one card every few seconds, back to the start at the end.
       Pauses on mouse hover, keyboard focus, for 6 s after a touch or arrow,
       while the popup is open, off screen or in a hidden tab. Never with
       reduced motion. */
    if (root.dataset.autorotate === 'true' && !reduceMotion) {
      const ms = Math.max(3, parseInt(root.dataset.speed, 10) || 4) * 1000;
      let hover = false;
      let focus = false;
      let onScreen = !('IntersectionObserver' in window);
      root.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hover = true; });
      root.addEventListener('pointerleave', () => { hover = false; });
      root.addEventListener('focusin', () => { focus = true; });
      root.addEventListener('focusout', () => { focus = false; });
      track.addEventListener('touchstart', hold, { passive: true });
      track.addEventListener('wheel', hold, { passive: true });
      if (!onScreen) new IntersectionObserver((en) => { onScreen = en.some((e) => e.isIntersecting); }, { threshold: 0.3 }).observe(track);
      window.setInterval(() => {
        if (!onScreen || hover || focus || Date.now() < holdUntil || document.hidden || (dialog && dialog.open)) return;
        const max = track.scrollWidth - track.clientWidth;
        if (max <= 1) return;
        if (track.scrollLeft >= max - 2) track.scrollTo({ left: 0, behavior: 'smooth' });
        else track.scrollBy({ left: step(), behavior: 'smooth' });
      }, ms);
    }

    /* Product filter */
    chips.forEach((chip) => chip.addEventListener('click', () => {
      hold();
      const f = chip.dataset.filter;
      chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      cards.forEach((c) => { c.hidden = !!f && c.dataset.product !== f; });
      track.scrollTo({ left: 0 });
      update();
    }));

    /* Cards rise in once, when the rail first comes on screen */
    if (!reduceMotion && 'IntersectionObserver' in window && !window.Shopify?.designMode) {
      cards.forEach((c, i) => c.style.setProperty('--i', Math.min(i, 6)));
      root.classList.add('is-armed');
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        root.classList.add('is-in');
        window.setTimeout(() => root.classList.remove('is-armed', 'is-in'), 1300);
      }, { threshold: 0.15 });
      io.observe(track);
    }

    /* Popup */
    if (!dialog) return;
    const dImg = dialog.querySelector('[data-img]');
    const dSource = dialog.querySelector('[data-source]');
    const dQuoteWrap = dialog.querySelector('[data-quote-wrap]');
    const dQuote = dialog.querySelector('[data-quote]');
    const dBy = dialog.querySelector('[data-by]');
    const dShop = dialog.querySelector('[data-shop]');
    const dCount = dialog.querySelector('[data-count]');
    let list = [];
    let idx = 0;

    const show = (i) => {
      idx = (i + list.length) % list.length;
      const b = list[idx].querySelector('[data-open]');
      dImg.src = b.dataset.full;
      dImg.alt = b.dataset.alt || '';
      dSource.textContent = b.dataset.source || '';
      dQuote.textContent = b.dataset.quote || '';
      dQuoteWrap.hidden = !b.dataset.quote;
      dBy.textContent = b.dataset.by || '';
      dBy.hidden = !b.dataset.by;
      if (b.dataset.url) {
        dShop.href = b.dataset.url;
        dShop.textContent = b.dataset.shop || '';
        dShop.hidden = false;
      } else {
        dShop.hidden = true;
        dShop.removeAttribute('href');
      }
      dCount.textContent = `${idx + 1} / ${list.length}`;
      dialog.querySelectorAll('[data-step]').forEach((s) => { s.hidden = list.length < 2; });
    };
    const open = (card) => {
      list = visible();
      show(Math.max(0, list.indexOf(card)));
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
    };
    const close = () => { if (dialog.open) dialog.close(); else dialog.removeAttribute('open'); };

    root.querySelectorAll('[data-open]').forEach((b) => b.addEventListener('click', () => open(b.closest('[data-card]'))));
    dialog.querySelector('[data-close]').addEventListener('click', close);
    dialog.querySelectorAll('[data-step]').forEach((s) => s.addEventListener('click', () => show(idx + +s.dataset.step)));
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') show(idx + 1);
      if (e.key === 'ArrowLeft') show(idx - 1);
    });
    let x0 = null;
    dImg.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    dImg.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 40 && list.length > 1) show(idx + (dx < 0 ? 1 : -1));
    }, { passive: true });
  };

  const boot = () => document.querySelectorAll('.ab-love').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => init(e.target.querySelector('.ab-love')));
  document.addEventListener('shopify:block:select', (e) => {
    const card = e.target.closest && e.target.closest('[data-card]');
    if (card && !card.hidden) card.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
  });
})();
